import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  Avatar,
  Box,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material';
import type React from 'react';
import type { SectionRevision } from '../../../types/tender';
import { BaseModal } from '../../common/UI';
import { getUserAvatarColor } from './utils';

interface HistoryDialogProps {
  open: boolean;
  onClose: () => void;
  revisions: SectionRevision[];
  onSelectRevision: (revision: SectionRevision) => void;
  isLoading: boolean;
}

const HistoryDialog: React.FC<HistoryDialogProps> = ({
  open,
  onClose,
  revisions,
  onSelectRevision,
  isLoading,
}) => {
  return (
    <BaseModal
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="primary" />
          Version History
        </Box>
      }
      cancelText="Close"
    >
      {isLoading ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography>Loading history...</Typography>
        </Box>
      ) : revisions.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">No revision history available.</Typography>
        </Box>
      ) : (
        <List>
          {revisions.map((rev, index) => {
            const date =
              rev.updatedAt instanceof Date
                ? rev.updatedAt
                : new Date(
                    (rev.updatedAt as { seconds?: number }).seconds
                      ? (rev.updatedAt as unknown as { seconds: number }).seconds * 1000
                      : (rev.updatedAt as number | string)
                  );

            const isLatest = index === 0;

            return (
              <ListItem
                key={rev.id}
                secondaryAction={
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RestoreIcon />}
                    onClick={() => onSelectRevision(rev)}
                  >
                    Compare
                  </Button>
                }
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                  bgcolor: isLatest ? 'action.hover' : 'inherit',
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: '0.875rem',
                      ...getUserAvatarColor(rev.editor?.displayName || 'Unknown'),
                    }}
                  >
                    {rev.editor?.displayName?.charAt(0) || '?'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {rev.editor?.displayName || 'Unknown User'}
                      {isLatest && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            bgcolor: 'success.light',
                            color: 'success.contrastText',
                            px: 0.5,
                            borderRadius: 1,
                            fontWeight: 'bold',
                          }}
                        >
                          LATEST
                        </Typography>
                      )}
                      {rev.revisionNumber === 1 && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            bgcolor: 'info.light',
                            color: 'info.contrastText',
                            px: 0.5,
                            borderRadius: 1,
                            fontWeight: 'bold',
                          }}
                        >
                          ORIGINAL
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={date.toLocaleString()}
                  primaryTypographyProps={{ fontWeight: 'bold', variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </BaseModal>
  );
};

export default HistoryDialog;
